import React, { useState } from "react";
import { Button, Dialog, DialogActions, DialogContent, DialogTitle, TextField } from "@mui/material";
import { useNotify } from "react-admin";

// Constants
import { PASSWORD_RULES_HINT, getPasswordStrengthError } from "../../constants/passwordRules";

type ResetPasswordButtonProps = {
  userId: string;
  dialogTitle?: string;
  onReset: (id: string, password: string) => Promise<void>;
};

const ResetPasswordButton: React.FC<ResetPasswordButtonProps> = ({ userId, dialogTitle = "Reset user password", onReset }) => {
  const notify = useNotify();
  const [open, setOpen] = useState(false);
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleClose = () => {
    setOpen(false);
    setPassword("");
    setError(null);
  };

  const handleSubmit = async () => {
    const validationError = getPasswordStrengthError(password);

    if (validationError) {
      setError(validationError);
      return;
    }

    setSubmitting(true);

    try {
      await onReset(userId, password);
      notify("Password reset successfully", { type: "success" });
      handleClose();
    } catch (err: any) {
      notify(err?.message || "Failed to reset password", { type: "error" });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <Button variant="outlined" onClick={() => setOpen(true)}>
        Reset Password
      </Button>
      <Dialog open={open} onClose={handleClose} fullWidth maxWidth="xs">
        <DialogTitle>{dialogTitle}</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            margin="dense"
            label="New password"
            type="password"
            value={password}
            onChange={(e) => {
              setPassword(e.target.value);
              setError(null);
            }}
            error={Boolean(error)}
            helperText={error ?? PASSWORD_RULES_HINT}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} disabled={submitting}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={submitting} variant="contained">
            Reset Password
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default ResetPasswordButton;
